import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Plus, Search, Filter, Eye, Edit, Trash, Users, Clock } from 'lucide-react';
import { useToast } from '../components/ui/use-toast';
import { useIsMobile } from '../hooks/use-mobile';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from '../lib/utils';
import { app } from "../firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { Subject } from '../components/course/types';
const courseImage = "https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=900&q=80";

const Courses = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [sampleCourses, setSmapleCourses] = useState<any[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
 

  const db = getFirestore(app);
  const auth = getAuth(app);

  useEffect(() => {
    const coursesUnsubscribe = onSnapshot(
      collection(db, "courses"),
      (querySnapshot) => {
        //   setLoader(true); // Show loader while fetching data
        const result = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Get the document ID  
          ...doc.data(), // Spread the document data  
        }));
        setFilteredCourses(result);
        setSmapleCourses(result);
        //   setLoader(false); // Hide loader after fetching data
      },
      (error) => {
        //  addToast(error.message, "error"); // Handle errors
        //  setLoader(false); // Hide loader in case of error
      }
    );
    return () => {
      coursesUnsubscribe();
    }
  }, [])
  const handleSearch = (e) => {
    const term = e?.target?.value?.toLowerCase();
    setSearchTerm(term);

    if (term === '') {
      setFilteredCourses(sampleCourses);
    } else {
      const filtered = sampleCourses.filter(course =>
        course?.title?.toLowerCase().includes(term) ||
        course?.courseName?.toLowerCase().includes(term) ||
        course?.category?.toLowerCase().includes(term) ||
        course?.instructor?.toLowerCase().includes(term)
      );
      setFilteredCourses(filtered);
    }
  };

  const filterByLevel = (level) => {
    if (level === 'all') {
      setFilteredCourses(sampleCourses);
    } else {
      const filtered = sampleCourses.filter(course =>
        course.level.toLowerCase() === level.toLowerCase()
      );
      setFilteredCourses(filtered);
    }
  };

  const handleDeleteCourse = (courseId) => {
    const updatedCourses = filteredCourses.filter(course => course.id !== courseId);
    setFilteredCourses(updatedCourses);

    toast({
      title: "Course deleted",
      description: "The course has been successfully deleted.",
    });
  };

  const navigateToCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Courses" subtitle="Manage your courses" />

      <div className="flex-1 p-4 md:p-8 animate-fade-in overflow-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              className="pl-10"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex gap-2">
                  <Filter size={16} />
                  <span>Filter</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white">
                <DropdownMenuLabel>Filter by Level</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => filterByLevel('all')}>
                  All Levels
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => filterByLevel('beginner')}>
                  Beginner
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => filterByLevel('intermediate')}>
                  Intermediate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => filterByLevel('advanced')}>
                  Advanced
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          
          </div>
        </div>

        {filteredCourses.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">No courses found. Try a different search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses?.map(course => (
              <Card key={course.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap justify-between items-start space-y-2">
                    <div className="relative w-full">
                      {/* Set a fixed aspect ratio for the container */}
                      <div className="aspect-w-16 aspect-h-9">
                        <img
                          src={course?.image ?? course?.thumbnail?.url}
                          alt={course.title}
                          className="w-full h-full object-contain transform transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      {/* <Badge className="absolute top-3 right-3 bg-pink-500">
                        Featured
                      </Badge> */}
                    </div>
                    <div className="p-0 w-full">
                      <h3 className="mt-2 text-md text-base font-medium line-clamp-2 h-12 text-balance">
                        {course.courseName}
                      </h3>
                    </div>
                    <div>
                      <StarRating rating={course.rating} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      <Users size={14} className="text-gray-400" />
                      <p className="text-gray-500">Students</p>
                      <p className="font-medium ml-2">{course?.students}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} className="text-gray-400" />
                      <p className="text-gray-500">Duration</p>
                      <p className="font-medium ml-2">{course?.duration}</p>
                    </div>
                    <div className="col-span-2 mt-1">
                      <p className="text-gray-500">Instructor</p>
                      <p className="font-medium">{course.instructor}</p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-bytesplash-blue hover:text-bytesplash-blue/90 hover:bg-bytesplash-soft-blue"
                    onClick={() => navigateToCourse(course.id)}
                  >
                    <Eye size={16} className="mr-1" />
                    View Details
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">Actions</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white">
                      <DropdownMenuItem onClick={() => navigate(`/courses/edit/${course.id}`)}>
                        <Edit size={14} className="mr-2" />
                        Edit Course
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigateToCourse(course.id)}>
                        <Users size={14} className="mr-2" />
                        View Students
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteCourse(course.id)}
                      >
                        <Trash size={14} className="mr-2" />
                        Delete Course
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;


const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={cn(
            "w-4 h-4",
            star <= Math.floor(rating) ? "text-yellow-400" : "text-gray-300"
          )}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 15.585l-6.918 3.636a.5.5 0 01-.726-.527l1.323-7.714L.146 7.357a.5.5 0 01.277-.857l7.744-1.125L10 0l2.833 5.375 7.744 1.125a.5.5 0 01.277.857l-3.533 3.623 1.323 7.714a.5.5 0 01-.726.527L10 15.585z"
            clipRule="evenodd"
          />
        </svg>
      ))}
      <span className="ml-1 text-sm text-gray-600">{rating} (280)</span>
    </div>
  );
};